'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useEffect, useRef } from 'react';
import { Header } from '../../components/Header';
import { RawHtml } from '../../components/RawHtml/RawHtml';
import { TheWrapper } from '../../components/TheWrapper';
import { EnumHeadingTextBlock } from '../../types/cmsEnums';
import { ComponentTextBlock } from './types';

export const PieceOfContent: React.FC<ComponentTextBlock> = ({
  title,
  title_size,
  content,
  id,
  anchorId,
  pushDataLayer,
}) => {
  // Editor-chosen anchor (CMS-1877). Rendered on a dedicated marker element so it
  // never clobbers TheWrapper's numeric `id`; Quick Answer Summary links here.
  const trimmedAnchorId = anchorId?.trim() || undefined;
  // GA click tracking for hyperlinks inside the rich-text content (CMS-1793).
  // The links are raw HTML (RawHtml → dangerouslySetInnerHTML), so they are not
  // React elements — catch clicks with a delegated listener on the container and
  // resolve the anchor with closest(). Payload matches the AirTable spec
  // (#2400 table / #2409 inline).
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = contentRef.current;
    if (!container || !pushDataLayer) return;

    const handleClick = (e: MouseEvent) => {
      // Use Element, not HTMLElement: clicking an SVG/icon inside a link makes
      // `e.target` an SVGElement, which must still resolve to its <a>.
      const target = e.target;
      if (!(target instanceof Element)) return;

      const link = target.closest('a');
      if (!link || !container.contains(link)) return;

      // Skip non-navigational anchors — empty href, or `javascript:` links such
      // as the "Show more" control RawHtml injects for long tables. Normalize
      // the href so casing/whitespace variants are still excluded.
      const href = (link.getAttribute('href') ?? '').trim();
      if (!href || href.toLowerCase().startsWith('javascript:')) return;

      // Collapse internal whitespace: anchors spanning nested elements/lines in
      // the raw HTML would otherwise keep newlines/indentation in the title.
      const clickText = (link.textContent ?? '').replace(/\s+/g, ' ').trim();
      const table = link.closest('table');

      if (table && container.contains(table)) {
        const cell = link.closest<HTMLTableCellElement>('td, th');
        const row = link.closest<HTMLTableRowElement>('tr');
        // index = column.row, both 0-based with the header row excluded (A&A QA
        // on CMS-1793). CMS rich-text tables have no <thead> — the header is
        // just the first <tr> of the (single) <tbody>, so section-relative
        // indexes don't skip it. `rowIndex` is the row's position across the
        // whole table; subtracting 1 drops the header row regardless of whether
        // it lives in a <thead> or is the first <tbody> row.
        pushDataLayer({
          event: 'navigate',
          component_type: 'table',
          component_title: `Table Text Link_${clickText}`,
          action: 'click',
          index:
            cell && row
              ? `${cell.cellIndex}.${Math.max(0, row.rowIndex - 1)}`
              : '',
        });
        return;
      }

      pushDataLayer({
        event: 'navigate',
        component_type: 'text-link',
        component_title: `Inline Text_${clickText}`,
        action: 'click',
        index: '',
      });
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [pushDataLayer]);

  return (
    <TheWrapper id={id} type="simple" customFull="mx-auto w-full">
      {trimmedAnchorId && (
        <span id={trimmedAnchorId} className="block" aria-hidden="true" />
      )}
      <div ref={contentRef} className="flex max-w-page-max flex-col">
        {title && (
          <Header
            className="!mb-4 md:!mb-6"
            level={title_size ?? EnumHeadingTextBlock.Heading_3}
          >
            {title}
          </Header>
        )}
        <RawHtml content={content} className="text-block-rich-text" />
      </div>
    </TheWrapper>
  );
};
